import { Component, input, output, forwardRef, signal, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ComponentSize } from '../types';

export interface AutocompleteOption {
  label: string;
  value: any;
  disabled?: boolean;
  icon?: string;
}

@Component({
  selector: 'app-autocomplete',
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true
    }
  ],
  template: `
    <div class="relative" #container>
      @if (label()) {
        <label 
          [for]="id() || name()"
          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <div class="relative">
        <input
          type="text"
          [id]="id() || name()"
          [name]="name()"
          [placeholder]="placeholder()"
          [disabled]="disabled()"
          [readonly]="readonly()"
          [class]="inputClasses"
          [value]="displayValue"
          (input)="onInput($event)"
          (focus)="onFocus()"
          (blur)="onBlur()"
          (keydown)="onKeyDown($event)"
          autocomplete="off" />
        
        @if (isOpen() && filteredOptions.length > 0) {
          <div
            #dropdownMenu
            [class]="menuClasses"
            role="listbox">
            @for (option of filteredOptions; track option.value; let i = $index) {
              <button
                type="button"
                role="option"
                [disabled]="option.disabled"
                [class]="getOptionClasses(option, i)"
                (click)="selectOption(option)"
                [attr.aria-selected]="isSelected(option)">
                @if (option.icon) {
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="option.icon" />
                  </svg>
                }
                <span class="flex-1 text-left">{{ option.label }}</span>
                @if (isSelected(option)) {
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                }
              </button>
            }
          </div>
        }
      </div>
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 mt-1 block font-medium">
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class AutocompleteComponent implements ControlValueAccessor {
  label = input<string>('');
  placeholder = input<string>('Type to search...');
  options = input<AutocompleteOption[]>([]);
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');
  size = input<ComponentSize>('md');
  minLength = input<number>(0);
  maxResults = input<number>(10);

  changeEvent = output<any>();

  @ViewChild('container', { static: false }) container!: ElementRef<HTMLElement>;
  @ViewChild('dropdownMenu', { static: false }) dropdownMenu!: ElementRef<HTMLElement>;

  value = signal<any>(null);
  inputValue = signal<string>('');
  isOpen = signal(false);
  highlightedIndex = signal(-1);
  touched = signal(false);

  private onChange = (value: any) => {};
  private onTouched = () => {};

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement;
    if (this.container?.nativeElement?.contains(target)) return;
    this.isOpen.set(false);
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.inputValue.set(newValue);
    
    if (newValue.length >= this.minLength()) {
      this.isOpen.set(true);
      this.highlightedIndex.set(-1);
    } else {
      this.isOpen.set(false);
      this.value.set(null);
      this.onChange(null);
    }
  }

  onFocus(): void {
    if (this.inputValue().length >= this.minLength() && this.filteredOptions.length > 0) {
      this.isOpen.set(true);
    }
  }

  onBlur(): void {
    setTimeout(() => {
      this.isOpen.set(false);
      this.markAsTouched();
      this.onTouched();
    }, 200);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen()) return;

    const options = this.filteredOptions;
    let newIndex = this.highlightedIndex();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newIndex = newIndex < options.length - 1 ? newIndex + 1 : 0;
        this.highlightedIndex.set(newIndex);
        break;
      case 'ArrowUp':
        event.preventDefault();
        newIndex = newIndex > 0 ? newIndex - 1 : options.length - 1;
        this.highlightedIndex.set(newIndex);
        break;
      case 'Enter':
        event.preventDefault();
        if (newIndex >= 0 && newIndex < options.length) {
          this.selectOption(options[newIndex]);
        }
        break;
      case 'Escape':
        this.isOpen.set(false);
        break;
    }
  }

  selectOption(option: AutocompleteOption): void {
    if (option.disabled) return;
    this.value.set(option.value);
    this.inputValue.set(option.label);
    this.onChange(option.value);
    this.changeEvent.emit(option.value);
    this.isOpen.set(false);
    this.markAsTouched();
  }

  isSelected(option: AutocompleteOption): boolean {
    return this.value() === option.value;
  }

  get filteredOptions(): AutocompleteOption[] {
    const searchTerm = this.inputValue().toLowerCase();
    if (!searchTerm || searchTerm.length < this.minLength()) {
      return [];
    }

    const filtered = this.options().filter(opt =>
      opt.label.toLowerCase().includes(searchTerm) && !opt.disabled
    );

    return filtered.slice(0, this.maxResults());
  }

  get displayValue(): string {
    if (this.value()) {
      const selected = this.options().find(opt => opt.value === this.value());
      return selected ? selected.label : this.inputValue();
    }
    return this.inputValue();
  }

  getOptionClasses(option: AutocompleteOption, index: number): string {
    const classes = [
      'w-full',
      'px-4',
      'py-2',
      'text-left',
      'flex',
      'items-center',
      'gap-2',
      'transition-colors',
      'duration-150',
      'focus:outline-none'
    ];

    if (option.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('hover:bg-gray-100', 'dark:hover:bg-gray-700', 'cursor-pointer');
    }

    if (this.highlightedIndex() === index) {
      classes.push('bg-gray-100', 'dark:bg-gray-700');
    }

    if (this.isSelected(option)) {
      classes.push('bg-blue-50', 'dark:bg-blue-900/20', 'text-blue-600', 'dark:text-blue-400');
    } else {
      classes.push('text-gray-700', 'dark:text-gray-300');
    }

    return classes.join(' ');
  }

  get inputClasses(): string {
    const classes = [
      'w-full',
      'px-4',
      'py-3',
      'border',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'placeholder:text-gray-400',
      'dark:placeholder:text-gray-500'
    ];

    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };
    classes.push(sizeMap[this.size()]);

    if (this.hasError) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-2', 'focus:ring-red-100', 'dark:focus:ring-red-900/30');
    } else {
      classes.push('border-gray-300', 'dark:border-gray-600', 'focus:border-blue-600', 'dark:focus:border-blue-500', 'focus:ring-2', 'focus:ring-blue-100', 'dark:focus:ring-blue-900/30');
    }

    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-900', 'dark:text-gray-100');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }

  get menuClasses(): string {
    return 'absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto';
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: any): void {
    this.value.set(value);
    if (value) {
      const selected = this.options().find(opt => opt.value === value);
      if (selected) {
        this.inputValue.set(selected.label);
      }
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }
}

